import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { isPlatformBrowser } from '@angular/common';
import { Observable, of, from } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AuthService } from '../../services/auth/auth.service';
import { MsalInitService } from '../../services/msal-init.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  constructor(
    private msalService: MsalService,
    private router: Router,
    private authService: AuthService,
    private msalInitService: MsalInitService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    
    if (!isPlatformBrowser(this.platformId)) {
      return true;
    }

    return from(this.msalInitService.ensureInitialized()).pipe(
      map(() => {
        try {
          const hasAccounts = this.msalService.instance.getAllAccounts().length > 0;
          const hasSessionId = this.getSessionStorageItem('Session_Id');
          const isAuthenticated = hasAccounts && hasSessionId;

          if (!isAuthenticated) {
            this.router.navigate(['/login']);
            return false;
          }

          this.setSessionStorageItem('sessionTimestamp', new Date().getTime().toString());
          return true;
        } catch (error) {
          console.error('Error checking authentication in AuthGuard:', error);
          this.router.navigate(['/login']);
          return false;
        }
      }),
      catchError((error) => {
        console.error('MSAL initialization error in AuthGuard:', error);
        this.router.navigate(['/login']);
        return of(false);
      })
    );
  }

  private getSessionStorageItem(key: string): string | null {
    if (isPlatformBrowser(this.platformId)) {
      try {
        return sessionStorage.getItem(key);
      } catch (error) {
        console.error('Error accessing sessionStorage:', error);
        return null;
      }
    }
    return null;
  }

  private setSessionStorageItem(key: string, value: string): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        sessionStorage.setItem(key, value);
      } catch (error) {
        console.error('Error setting sessionStorage:', error);
      }
    }
  }
}